import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminConfigApi from "../../api/admin/AdminConfigApi";
import _ from "lodash";
import { AllowIpDto, ConfigDto, FwocDto } from "../../../types/adminConfig";
import { useTranslation } from "react-i18next";

const initialState = {
  config: {
    activeHtmlWrapping: false,
    allowMacOS: false,
    allowMobile: true,
    autoUnwrappingPermission: false,
    companyName: null,
    externalUserViewType: "MY_LIST",
    fileOwnerSelectPermission: "FILEUPLOADER",
    fileUploadMaxSizeMB: 200,
    fileUploadPermission: "EXTERNAL",
    licenseAlertPercentage: 80,
    mobilePushKeyType: "2022",
    restorePermission: "MASTER",
    sentMail: false,
    folderMoveOwner: false,
    serviceType: "CLOUD",
    unwrappingPermission: "MASTER",
    userOtpType: "NONE",
    watermark: "ALL",
    watermarkFontSize: 16,
    watermarkOpacity: 0.2,
    watermarkPattern:
      "${username}(${userid}) / ${contentname} / ${currenttime} / ",
    watermarkRotate: -30,
    watermarkTop: 59,
    webOfficeAllowUserType: "ALL",
    webOfficeType: "COLLABORA",
    workgroupCreatePermission: "INTERNAL",
    workgroupHeadSelectPermission: "@@WORKGROUPCREATOR",
    lastUpdateTime: 0,
    notificationPreferencePolicyDTO: {
      usingWorkgroupInvitedAlert: true,
      usingFileAlert: true,
      fileAlert: ["fileshare", "memo", "revision"],
      usingReadRequestAlert: true,
      readRequestAlert: ["readRequest", "readRequestConfirm"],
    },
    workgroupPermissionDTO: {
      changeCode: "CREATE",
      uniqueId: null,
      workgroupCode: null,
      syncId: "NONE",
      targetType: "WORKGROUP",
      targetValue: null,
      authType: "LOGIN",
      policyType: "TEMPLATE",
      policyCode: null,
      updateTime: 1,
      createUser: "",
      updateUser: "",
      policy: {
        startTime: 0,
        allowDays: 0,
        allowDaysTimeZone: 9.0,
        allowViewCount: 0,
        currentViewCount: 0,
        allowDownloadCount: 0,
        currentDownloadCount: 0,
        fileShareLinkPolicy: {
          authType: "LOGIN",
          password: "",
          urlAction: "WEBUSING_AND_DOWNLOAD",
          allowDays: 61,
          allowDaysTimeZone: 9,
        },
        drmPerm: ["view", "print", "print_screen", "extract"],
        webView: true,
        revision: true,
        download: true,
        wrapping: true,
      },
    },
  } as ConfigDto,
  fwoc: {
    defaultWebOffice: "",
    oosUse: false,
    oosUrl: false,
    office365Use: false,
    office365Url: "",
    collaboraUse: false,
    collaboraUrl: "",
    tfsiUse: false,
    tfsiUrl: "",
  } as FwocDto,
  allowIp: {
    allowIp: [] as AllowIpDto[],
    errorMessage: "",
    errorCode: "",
  },
};

export const trunkGetConfig = createAsyncThunk(
  "wapi/admins/config/get",
  async () => {
    const result = await adminConfigApi.getConfig();
    return result;
  }
);

export const trunkSetConfig = createAsyncThunk(
  "wapi/admins/config/set",
  async (config: ConfigDto) => {
    const result = await adminConfigApi.setConfig(config);
    return result;
  }
);

export const trunkGetFwocConfig = createAsyncThunk(
  "wapi/admins/config/fwoc",
  async () => {
    const result = await adminConfigApi.getFwocConfig();
    return result;
  }
);
export const trunkGetIpConfig = createAsyncThunk(
  "wapi/admins/config/access/control/get",
  async () => {
    const result = await adminConfigApi.getIpConfig();
    return result;
  }
);
export const trunkSetIpConfig = createAsyncThunk(
  "wapi/admins/config/access/control/set",
  async (newIp: AllowIpDto[]) => {
    const ipResult = await adminConfigApi.setIpConfig(newIp);
    return ipResult;
  }
);

const adminConfigSlice = createSlice({
  name: "adminConfig",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(trunkGetConfig.fulfilled, (state, action) => {
        state.config.externalUserViewType = action.payload.externalUserViewType;
        state.config.webOfficeAllowUserType =
          action.payload.webOfficeAllowUserType;
        state.config.restorePermission = action.payload.restorePermission;
        state.config.unwrappingPermission = action.payload.unwrappingPermission;
        state.config.watermark = action.payload.watermark;
        state.config.webOfficeType = action.payload.webOfficeType;
        state.config.fileUploadMaxSizeMB = action.payload.fileUploadMaxSizeMB;
        state.config.licenseAlertPercentage =
          action.payload.licenseAlertPercentage;
        state.config.sentMail = action.payload.sentMail;
        state.config.lastUpdateTime = action.payload.lastUpdateTime;
        state.config.companyName = action.payload.companyName;
        state.config.serviceType = action.payload.serviceType;
        state.config.allowMobile = action.payload.allowMobile;
        state.config.allowMacOS = action.payload.allowMacOS;
        state.config.watermarkPattern = action.payload.watermarkPattern;
        state.config.notificationPreferencePolicyDTO =
          action.payload.notificationPreferencePolicyDTO;
        state.config.workgroupCreatePermission =
          action.payload.workgroupCreatePermission;
        state.config.workgroupHeadSelectPermission =
          action.payload.workgroupHeadSelectPermission;
        state.config.fileUploadPermission = action.payload.fileUploadPermission;
        state.config.fileOwnerSelectPermission =
          action.payload.fileOwnerSelectPermission;
        state.config.autoUnwrappingPermission =
          action.payload.autoUnwrappingPermission;
        state.config.activeHtmlWrapping = action.payload.activeHtmlWrapping;
        state.config.userOtpType = action.payload.userOtpType;
        state.config.internalUserOtpType = action.payload.internalUserOtpType;
        state.config.externalUserOtpType = action.payload.externalUserOtpType;
        state.config.watermarkFontSize = action.payload.watermarkFontSize;
        state.config.watermarkOpacity = action.payload.watermarkOpacity;
        state.config.watermarkRotate = action.payload.watermarkRotate;
        state.config.watermarkTop = action.payload.watermarkTop;
        state.config.folderMoveOwner = action.payload.folderMoveOwner;
        state.config.folderMoveAllowType = action.payload.folderMoveAllowType;
        state.config.applicationPlatformPolicyDTO =
          action.payload.applicationPlatformPolicyDTO;
        state.config.workgroupPermissionDTO =
          action.payload.workgroupPermissionDTO;
      })
      .addCase(trunkGetConfig.rejected, (state, action) => {
        state.config.errorMessage = action.error.message;
      })
      .addCase(trunkSetConfig.fulfilled, (state, action) => {
        state.config.externalUserViewType = action.payload.externalUserViewType;
        state.config.webOfficeAllowUserType =
          action.payload.webOfficeAllowUserType;
        state.config.restorePermission = action.payload.restorePermission;
        state.config.unwrappingPermission = action.payload.unwrappingPermission;
        state.config.watermark = action.payload.watermark;
        state.config.webOfficeType = action.payload.webOfficeType;
        state.config.fileUploadMaxSizeMB = action.payload.fileUploadMaxSizeMB;
        state.config.licenseAlertPercentage =
          action.payload.licenseAlertPercentage;
        state.config.sentMail = action.payload.sentMail;
        state.config.lastUpdateTime = action.payload.lastUpdateTime;
        state.config.companyName = action.payload.companyName;
        state.config.serviceType = action.payload.serviceType;
        state.config.allowMobile = action.payload.allowMobile;
        state.config.allowMacOS = action.payload.allowMacOS;
        state.config.watermarkPattern = action.payload.watermarkPattern;
        state.config.notificationPreferencePolicyDTO =
          action.payload.notificationPreferencePolicyDTO;
        state.config.workgroupCreatePermission =
          action.payload.workgroupCreatePermission;
        state.config.workgroupHeadSelectPermission =
          action.payload.workgroupHeadSelectPermission;
        state.config.fileUploadPermission = action.payload.fileUploadPermission;
        state.config.fileOwnerSelectPermission =
          action.payload.fileOwnerSelectPermission;
        state.config.autoUnwrappingPermission =
          action.payload.autoUnwrappingPermission;
        state.config.activeHtmlWrapping = action.payload.activeHtmlWrapping;
        state.config.userOtpType = action.payload.userOtpType;
        state.config.internalUserOtpType = action.payload.internalUserOtpType;
        state.config.externalUserOtpType = action.payload.externalUserOtpType;
        state.config.watermarkFontSize = action.payload.watermarkFontSize;
        state.config.watermarkOpacity = action.payload.watermarkOpacity;
        state.config.watermarkRotate = action.payload.watermarkRotate;
        state.config.watermarkTop = action.payload.watermarkTop;
        state.config.folderMoveOwner = action.payload.folderMoveOwner;
        state.config.folderMoveAllowType = action.payload.folderMoveAllowType;
        state.config.applicationPlatformPolicyDTO =
          action.payload.applicationPlatformPolicyDTO;
        state.config.workgroupPermissionDTO =
          action.payload.workgroupPermissionDTO;
      })
      .addCase(trunkSetConfig.rejected, (state, action) => {
        const { t } = useTranslation();
        state.config.errorMessage = t("admin.common.alert.fail");
      });
    builder
      .addCase(trunkGetFwocConfig.fulfilled, (state, action) => {
        state.fwoc.defaultWebOffice = action.payload.defaultWebOffice;
        state.fwoc.oosUse = action.payload.oosUse;
        state.fwoc.oosUrl = action.payload.oosUrl;
        state.fwoc.office365Use = action.payload.office365Use;
        state.fwoc.office365Url = action.payload.office365Url;
        state.fwoc.collaboraUse = action.payload.collaboraUse;
        state.fwoc.collaboraUrl = action.payload.collaboraUrl;
        state.fwoc.tfsiUse = action.payload.tfsiUse;
        state.fwoc.tfsiUrl = action.payload.tfsiUrl;
      })
      .addCase(trunkGetFwocConfig.rejected, (state, action) => {
        state.fwoc.errorMessage = action.error.message;
      });
    builder
      .addCase(trunkGetIpConfig.fulfilled, (state, action) => {
        state.allowIp.allowIp = action.payload;
      })
      .addCase(trunkGetIpConfig.rejected, (state, action) => {
        if (action.error.message && action.error.code) {
          state.allowIp.errorMessage = action.error.message;
          state.allowIp.errorCode = action.error.code;
        }
      })
      .addCase(trunkSetIpConfig.fulfilled, (state, action) => {
        state.allowIp.allowIp = action.payload;
      })
      .addCase(trunkSetIpConfig.rejected, (state, action) => {
        if (action.error.message && action.error.code) {
          state.allowIp.errorMessage = action.error.message;
          state.allowIp.errorCode = action.error.code;
        }
      });
  },
});

export const adminConfigActions = adminConfigSlice.actions;
export default adminConfigSlice.reducer;
